import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { JobBoard } from "./JobBoard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHEF_STATUS = { pending: "Offen", approved: "Freigegeben", rejected: "Abgelehnt" };
const REQ_STATUS = {
  new: "Neu", in_progress: "In Bearbeitung", chef_proposed: "Koch vorgeschlagen",
  contract_sent: "Vertrag versandt", contract_signed: "Vertrag unterschrieben",
  placed: "Vermittelt", declined: "Abgelehnt",
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "—");
const BADGE = "px-2 py-1 text-[10px] uppercase tracking-[0.12em] border";

export const VermittlungTab = ({ pwd }) => {
  const [view, setView] = useState("koeche");
  const [chefs, setChefs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stamm, setStamm] = useState({ bereiche: {}, positionen: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = { headers: { "X-Admin-Password": pwd } };
      const [c, r, s] = await Promise.all([
        axios.get(`${API}/admin/vermittlung/koeche`, h),
        axios.get(`${API}/admin/vermittlung/anfragen`, h),
        axios.get(`${API}/vermittlung/stammdaten`),
      ]);
      setChefs(c.data);
      setRequests(r.data);
      setStamm(s.data);
    } catch {
      toast.error("Vermittlungsdaten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const openFile = (fileId) => {
    // Direkter Link mit Passwort als Query — öffnet PDF/Bild sofort im neuen Tab
    window.open(`${API}/admin/vermittlung/dateien/${fileId}?pwd=${encodeURIComponent(pwd)}`,
      "_blank", "noreferrer");
  };

  const approveChef = async (chef) => {
    if (!window.confirm(`${chef.vorname} ${chef.nachname} freigeben?\n\nEr/sie bekommt sofort die Aufnahme-Bestätigung per E-Mail.`)) return;
    try {
      await axios.post(`${API}/admin/vermittlung/koeche/${chef.id}/approve`, {}, { headers: { "X-Admin-Password": pwd } });
      toast.success(`${chef.vorname} ${chef.nachname} ist im Netzwerk`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Freigabe fehlgeschlagen");
    }
  };

  const patchChef = async (id, body) => {
    try {
      await axios.patch(`${API}/admin/vermittlung/koeche/${id}`, body, { headers: { "X-Admin-Password": pwd } });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const deleteRequest = async (r) => {
    if (!window.confirm(
      `Anfrage ${r.request_number} von ${r.betrieb} endgültig löschen?\n\n` +
      `Auch die signierte Provisionsvereinbarung und zugehörige Einsätze werden entfernt. ` +
      `Das lässt sich nicht rückgängig machen.`
    )) return;
    try {
      const res = await axios.delete(`${API}/admin/vermittlung/anfragen/${r.id}`,
        { headers: { "X-Admin-Password": pwd } });
      toast.success(`Anfrage von ${res.data.betrieb} gelöscht`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const deleteChef = async (chef) => {
    if (!window.confirm(
      `Registrierung von ${chef.vorname} ${chef.nachname} (${chef.chef_number}) endgültig löschen?\n\n` +
      `Profil, alle Unterlagen und offene Einsatz-Anfragen werden entfernt. Das lässt sich nicht rückgängig machen.\n\n` +
      `Tipp: Falls du die Unterlagen behalten willst, schick sie dir vorher mit "Unterlagen an mich mailen" zu.`
    )) return;
    try {
      const r = await axios.delete(`${API}/admin/vermittlung/koeche/${chef.id}`,
        { headers: { "X-Admin-Password": pwd } });
      toast.success(`${r.data.name} gelöscht (${r.data.dateien_entfernt} Datei(en) entfernt)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const resendDocs = async (chef) => {
    if (!window.confirm(`Alle Unterlagen von ${chef.vorname} ${chef.nachname} an dich mailen?\n\nEnthält Gewerbeanmeldung, Lebenslauf, § 43-Belehrung und den unterschriebenen Vertrag als Anhang.`)) return;
    try {
      const r = await axios.post(`${API}/admin/vermittlung/koeche/${chef.id}/unterlagen-senden`, {},
        { headers: { "X-Admin-Password": pwd } });
      toast.success(`${r.data.dokumente} Dokument(e) an dich versandt`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
    }
  };

  const portalLink = async (chef, reset) => {
    if (reset && !window.confirm(`Persönlichen Link von ${chef.vorname} ${chef.nachname} zurücksetzen?\n\nDer alte Link funktioniert dann nicht mehr — z. B. wenn der Koch ausgeschieden ist.`)) return;
    try {
      const r = await axios.get(`${API}/admin/vermittlung/koeche/${chef.id}/portal-link${reset ? "?reset=true" : ""}`,
        { headers: { "X-Admin-Password": pwd } });
      await navigator.clipboard.writeText(r.data.portal_url);
      toast.success(reset ? "Neuer Link erstellt und kopiert" : "Koch-Link kopiert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Link konnte nicht erstellt werden");
    }
  };

  const patchRequest = async (id, body) => {
    try {
      const r = await axios.patch(`${API}/admin/vermittlung/anfragen/${id}`, body, { headers: { "X-Admin-Password": pwd } });
      if (r.data?.invoice_warning) toast.error(r.data.invoice_warning);
      else if (r.data?.provision_invoice_number && body.status === "placed")
        toast.success(`Vermittelt ✓ — Provisionsrechnung ${r.data.provision_invoice_number} versandt`);
      else toast.success("Gespeichert");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const recheckUid = async (req) => {
    try {
      const r = await axios.post(`${API}/admin/vermittlung/anfragen/${req.id}/uid-pruefen`, {},
        { headers: { "X-Admin-Password": pwd } });
      toast[r.data.status === "valid" ? "success" : "error"](r.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "UID-Prüfung fehlgeschlagen");
    }
  };

  const askUid = async (req) => {
    const v = window.prompt("UID-Nummer des Betriebs (z. B. ATU12345678)", req.ust_id || "");
    if (v === null) return;
    patchRequest(req.id, { ust_id: v.trim() });
  };

  const createInvoice = async (req, force = false) => {
    const isPerm = req.placement_type === "permanent";
    const amount = isPerm
      ? (req.provision_fee_eur || stamm.placement_fee_eur || 100)
      : (req.provision_rate_eur || 40) * (req.provision_days || req.einsatztage || 1) * (req.anzahl_koeche || 1);
    if (!window.confirm(
      `Provisionsrechnung über ${amount.toFixed(2)} € netto an ${req.betrieb} erstellen?\n\n` +
      `Die Rechnung wird in LexOffice angelegt und sofort per E-Mail an ${req.email} verschickt.`
    )) return;
    try {
      const r = await axios.post(
        `${API}/admin/vermittlung/anfragen/${req.id}/provisionsrechnung${force ? "?force=true" : ""}`,
        {}, { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(`Rechnung ${r.data.provision_invoice_number} erstellt und versandt`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Provisionsrechnung fehlgeschlagen");
    }
  };

  const sendInvoice = async (req, toAdmin) => {
    if (!toAdmin && !window.confirm(`Rechnung ${req.provision_invoice_number} an ${req.email} senden?`)) return;
    try {
      await axios.post(
        `${API}/admin/vermittlung/anfragen/${req.id}/provisionsrechnung/senden${toAdmin ? "?to_admin=true" : ""}`,
        {}, { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(toAdmin ? "Kontroll-Kopie an dich versandt" : `Rechnung an ${req.betrieb} versandt`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
    }
  };

  const invoicePdf = async (req) => {
    try {
      const resp = await axios.get(`${API}/admin/vermittlung/anfragen/${req.id}/provisionsrechnung.pdf`, {
        headers: { "X-Admin-Password": pwd }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Provisionsrechnung_${req.provision_invoice_number || req.request_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF konnte nicht erstellt werden");
    }
  };

  const sendSignRequest = async (req, toAdmin) => {
    const isPerm = req.placement_type === "permanent";
    const amount = isPerm
      ? (req.provision_fee_eur || stamm.placement_fee_eur || 100)
      : (req.provision_rate_eur || 40) * (req.provision_days || req.einsatztage || 1) * (req.anzahl_koeche || 1);
    if (!toAdmin && !window.confirm(
      `Provisionsvereinbarung über ${amount.toFixed(2)} € netto an ${req.email} senden?\n\n` +
      `${req.ansprechpartner} kann direkt im Browser unterschreiben. Du bekommst eine BCC-Kopie.`
    )) return;
    try {
      const r = await axios.post(
        `${API}/admin/vermittlung/anfragen/${req.id}/vertrag/senden${toAdmin ? "?to_admin=true" : ""}`,
        {}, { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(toAdmin ? "Kontroll-Kopie an dich versandt" : `Unterschreiben-Link an ${req.betrieb} versandt`);
      if (toAdmin && r.data?.sign_url) console.log("Sign-Link:", r.data.sign_url);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
    }
  };

  const copySignLink = async (req) => {
    try {
      const r = await axios.get(`${API}/admin/vermittlung/anfragen/${req.id}/vertrag/link`, {
        headers: { "X-Admin-Password": pwd },
      });
      await navigator.clipboard.writeText(r.data.sign_url);
      toast.success("Unterschreiben-Link kopiert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Link konnte nicht kopiert werden");
    }
  };

  const contractPdf = async (req) => {
    try {
      const resp = await axios.get(`${API}/admin/vermittlung/anfragen/${req.id}/provisionsvertrag.pdf`, {
        headers: { "X-Admin-Password": pwd }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Provisionsvereinbarung_${req.request_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF konnte nicht erstellt werden");
    }
  };

  const labels = (keys, map) => (keys || []).map((k) => map[k] || k).join(", ") || "—";
  const pendingCount = chefs.filter((c) => c.status === "pending").length;
  const openCount = requests.filter((r) => !["placed", "declined"].includes(r.status)).length;

  return (
    <div data-testid="vermittlung-tab">
      <div className="flex flex-wrap gap-2 mb-8">
        {[["koeche", `Mietköche (${chefs.length})${pendingCount ? ` · ${pendingCount} offen` : ""}`],
          ["anfragen", `Vermittlungsanfragen (${requests.length})${openCount ? ` · ${openCount} offen` : ""}`],
          ["einsaetze", "Einsätze / Job-Board"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`vermittlung-view-${k}`}
            className={`px-5 py-3 text-[11px] uppercase tracking-[0.15em] border transition-colors ${view === k ? "bg-[#2C2A26] text-white border-[#2C2A26]" : "bg-white border-[#E3DECE] text-[#575349] hover:border-[#BA6D52]"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-[#757067]">Lade …</p>}

      {!loading && view === "einsaetze" && (
        <JobBoard pwd={pwd} stamm={stamm} requests={requests} />
      )}

      {!loading && view === "koeche" && (
        chefs.length === 0 ? (
          <p className="text-sm text-[#757067]">Noch keine Registrierungen.</p>
        ) : (
          <div className="space-y-4">
            {chefs.map((c) => (
              <div key={c.id} className="bg-white border border-[#E3DECE] p-6" data-testid={`chef-card-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif text-xl">{c.vorname} {c.nachname}</h3>
                      <span className={`${BADGE} ${c.status === "approved" ? "border-green-700 text-green-800 bg-green-50" : c.status === "rejected" ? "border-[#E3DECE] text-[#757067]" : "border-[#BA6D52] text-[#BA6D52] bg-[#FAF5F1]"}`}>
                        {CHEF_STATUS[c.status] || c.status}
                      </span>
                      <span className="text-[11px] text-[#757067]">{c.chef_number}</span>
                    </div>
                    <p className="text-sm text-[#575349] mt-2">
                      {c.plz} {c.ort}{c.land === "AT" ? " · Österreich" : ""} · {c.umkreis_km} km Umkreis · {c.erfahrung_jahre ? `${c.erfahrung_jahre} J. Erfahrung` : "Erfahrung n. a."}
                    </p>
                    <p className="text-sm text-[#757067] mt-1">
                      <a href={`mailto:${c.email}`} className="hover:text-[#BA6D52]">{c.email}</a> ·{" "}
                      <a href={`tel:${c.telefon}`} className="hover:text-[#BA6D52]">{c.telefon}</a>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {c.status === "pending" && (
                      <button onClick={() => approveChef(c)} data-testid={`chef-approve-${c.id}`}
                        className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                        Freigeben & benachrichtigen
                      </button>
                    )}
                    {c.status !== "rejected" && (
                      <button onClick={() => patchChef(c.id, { status: "rejected" })} data-testid={`chef-reject-${c.id}`}
                        className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                        Ablehnen
                      </button>
                    )}
                    {c.status === "rejected" && (
                      <button onClick={() => patchChef(c.id, { status: "pending" })} data-testid={`chef-reopen-${c.id}`}
                        className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                        Wieder öffnen
                      </button>
                    )}
                    {c.status === "approved" && (
                      <>
                        <button onClick={() => portalLink(c, false)} data-testid={`chef-portal-link-${c.id}`}
                          className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                          Koch-Link kopieren
                        </button>
                        <button onClick={() => portalLink(c, true)} data-testid={`chef-portal-reset-${c.id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:border-red-700 hover:text-red-700 px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                          Link zurücksetzen
                        </button>
                      </>
                    )}
                    <button onClick={() => resendDocs(c)} data-testid={`chef-resend-docs-${c.id}`}
                      className="border border-[#E3DECE] text-[#575349] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                      Unterlagen an mich mailen
                    </button>
                    <button onClick={() => deleteChef(c)} data-testid={`chef-delete-${c.id}`}
                      className="border border-[#E3DECE] text-[#757067] hover:border-red-700 hover:text-red-700 px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                      Registrierung löschen
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-5 pt-5 border-t border-[#E3DECE] text-sm">
                  <Row label="Bereiche" value={labels(c.bereiche, stamm.bereiche)} />
                  <Row label="Positionen" value={labels(c.positionen, stamm.positionen)} />
                  <Row label="Preise" value={[c.stundensatz_ab_eur && `ab ${c.stundensatz_ab_eur} €/h`, c.tagessatz_ab_eur && `ab ${c.tagessatz_ab_eur} €/Tag`].filter(Boolean).join(" · ") || "—"} />
                  <Row label="Sucht" value={[c.sucht_tageseinsaetze && "Tageseinsätze", c.sucht_festanstellung && `Festanstellung${c.wunschgehalt_brutto_monat ? ` (${c.wunschgehalt_brutto_monat} € brutto)` : ""}`].filter(Boolean).join(" · ") || "—"} />
                  <Row label="Verfügbar ab" value={fmt(c.verfuegbar_ab)} />
                  <Row label="Mobil / Werkzeug" value={`${c.fuehrerschein ? "Führerschein" : "kein Führerschein"} · ${c.eigenes_werkzeug ? "eigenes Werkzeug" : "kein eigenes Werkzeug"}`} />
                  <Row label="Registriert" value={fmt(c.created_at)} />
                  <Row label="Unterschrift" value={c.signature ? "digital signiert ✓" : "PDF hochgeladen"} />
                </div>

                {c.ueber_mich && (
                  <p className="text-sm text-[#575349] mt-4 pt-4 border-t border-[#E3DECE] whitespace-pre-line leading-relaxed">{c.ueber_mich}</p>
                )}

                <div className="mt-5 pt-5 border-t border-[#E3DECE]">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#757067]">Dokumente</p>
                    {(() => {
                      const need = [["gewerbeanmeldung", "Gewerbeanmeldung"], ["lebenslauf", "Lebenslauf"],
                        ["gesundheitszeugnis", "§ 43 IfSG"]];
                      const missing = need.filter(([k]) => !(c.documents || {})[k]).map(([, l]) => l);
                      const hasContract = !!(c.documents || {}).vermittlungsvertrag_signiert
                        || !!(c.documents || {}).vermittlungsvertrag;
                      if (!missing.length && hasContract) {
                        return <span className={`${BADGE} border-green-700 text-green-800 bg-green-50`}
                          data-testid={`chef-docs-complete-${c.id}`}>Unterlagen komplett ✓</span>;
                      }
                      const ifsgLater = c.gesundheitszeugnis_nachreichen && !(c.documents || {}).gesundheitszeugnis;
                      const missingLabels = missing.map((l) => (l === "§ 43 IfSG" && ifsgLater ? "§ 43 IfSG (wird nachgereicht)" : l));
                      return <span className={`${BADGE} border-amber-600 text-amber-800 bg-amber-50`}
                        data-testid={`chef-docs-missing-${c.id}`}>
                        Fehlt: {[...missingLabels, ...(hasContract ? [] : ["Vertrag"])].join(", ")}
                      </span>;
                    })()}
                    <button onClick={() => patchChef(c.id, { sucht_privatkunden: !c.sucht_privatkunden })}
                      data-testid={`chef-privat-toggle-${c.id}`}
                      className={`${BADGE} transition-colors ${c.sucht_privatkunden
                        ? "border-[#BA6D52] text-[#BA6D52] bg-[#FAF5F1]"
                        : "border-[#E3DECE] text-[#757067] hover:border-[#BA6D52]"}`}>
                      {c.sucht_privatkunden ? "Privatkunden ✓" : "Privatkunden: nein"}
                    </button>
                    {c.gesundheitszeugnis_nachreichen && (
                      <button onClick={() => patchChef(c.id, { gesundheitszeugnis_nachreichen: false })}
                        data-testid={`chef-ifsg-received-${c.id}`}
                        className="border border-[#E3DECE] hover:border-[#BA6D52] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-[#575349]">
                        § 43 IfSG liegt vor
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(c.documents || {}).map(([kind, d]) => (
                      <button key={d.file_id} onClick={() => openFile(d.file_id, d.filename)}
                        data-testid={`chef-doc-${c.id}-${kind}`}
                        className="border border-[#E3DECE] hover:border-[#BA6D52] px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-[#575349]">
                        ⬇ {kind.replace(/_/g, " ")}
                      </button>
                    ))}
                    {!Object.keys(c.documents || {}).length && <span className="text-sm text-[#757067]">— keine —</span>}
                  </div>
                </div>

                <NotesField initial={c.admin_notes} onSave={(v) => patchChef(c.id, { admin_notes: v })} testId={`chef-notes-${c.id}`} />
              </div>
            ))}
          </div>
        )
      )}

      {!loading && view === "anfragen" && (
        requests.length === 0 ? (
          <p className="text-sm text-[#757067]">Noch keine Vermittlungsanfragen.</p>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => {
              const isTemp = r.placement_type === "temporary";
              const provision = isTemp
                ? (r.provision_rate_eur || 40) * (r.provision_days || r.einsatztage || 1) * (r.anzahl_koeche || 1)
                : (r.provision_fee_eur || stamm.placement_fee_eur || 100);
              return (
                <div key={r.id} className="bg-white border border-[#E3DECE] p-6" data-testid={`request-card-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-serif text-xl">{r.betrieb}</h3>
                        <span className={`${BADGE} ${r.status === "placed" ? "border-green-700 text-green-800 bg-green-50" : r.status === "declined" ? "border-[#E3DECE] text-[#757067]" : "border-[#BA6D52] text-[#BA6D52] bg-[#FAF5F1]"}`}>
                          {REQ_STATUS[r.status] || r.status}
                        </span>
                        <span className={`${BADGE} border-[#E3DECE] text-[#757067]`}>{isTemp ? "Mietkoch" : "Festanstellung"}</span>
                        <span className="text-[11px] text-[#757067]">{r.request_number}</span>
                      </div>
                      <p className="text-sm text-[#575349] mt-2">
                        {r.customer_type === "private" ? (
                          <span className="inline-block mr-2 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] border border-[#BA6D52] text-[#BA6D52]"
                            data-testid={`req-type-privat-${r.id}`}>Privatkunde</span>
                        ) : null}
                        {r.customer_type === "private" ? (r.anlass || "Privater Anlass") : r.betriebsart}
                        {" · "}{r.plz} {r.ort}{r.land === "AT" ? " · Österreich" : ""}
                        {r.customer_type === "private" && r.personen ? ` · ${r.personen} Personen` : ""}
                      </p>
                      {r.land === "AT" && r.customer_type !== "private" && (
                        <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`req-uid-${r.id}`}>
                          <span className={`${BADGE} ${
                            r.ust_id_check?.status === "valid" ? "border-green-700 text-green-800 bg-green-50"
                              : r.ust_id_check?.status === "invalid" ? "border-red-700 text-red-800 bg-red-50"
                              : "border-amber-600 text-amber-800 bg-amber-50"}`}>
                            {!r.ust_id ? "UID fehlt"
                              : r.ust_id_check?.status === "valid" ? `UID ${r.ust_id} · im EU-Register bestätigt ✓`
                              : r.ust_id_check?.status === "invalid" ? `UID ${r.ust_id} · nicht gültig`
                              : `UID ${r.ust_id} · ungeprüft`}
                          </span>
                          <button onClick={() => askUid(r)} data-testid={`req-uid-edit-${r.id}`}
                            className="border border-[#E3DECE] hover:border-[#BA6D52] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-[#575349]">
                            {r.ust_id ? "UID ändern" : "UID eintragen"}
                          </button>
                          {r.ust_id && (
                            <button onClick={() => recheckUid(r)} data-testid={`req-uid-check-${r.id}`}
                              className="border border-[#E3DECE] hover:border-[#BA6D52] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-[#575349]">
                              Im EU-Register prüfen
                            </button>
                          )}
                        </div>
                      )}
                      <p className="text-sm text-[#757067] mt-1">
                        {r.ansprechpartner} ·{" "}
                        <a href={`mailto:${r.email}`} className="hover:text-[#BA6D52]">{r.email}</a> ·{" "}
                        <a href={`tel:${r.telefon}`} className="hover:text-[#BA6D52]">{r.telefon}</a>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#757067]">Provision</p>
                      <p className="font-serif text-2xl mt-1">{provision ? `${provision.toFixed(2)} €` : "—"}</p>
                      <p className="text-[11px] text-[#757067]">
                        {r.customer_type === "private" ? "netto · Kunde zahlt brutto"
                          : r.land === "AT" ? "netto · Reverse Charge" : "netto zzgl. MwSt."}
                      </p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-5 pt-5 border-t border-[#E3DECE] text-sm">
                    {isTemp ? (
                      <>
                        <Row label="Zeitraum" value={`${fmt(r.von_datum)} – ${fmt(r.bis_datum)}`} />
                        <Row label="Einsatztage / Köche" value={`${r.einsatztage} Tage · ${r.anzahl_koeche} Koch/Köche`} />
                        <Row label="Tageszeit" value={r.taeglich_von ? `${r.taeglich_von} – ${r.taeglich_bis}` : "—"} />
                        <Row label="Budget" value={r.budget_stundensatz_eur ? `${r.budget_stundensatz_eur} €/h netto` : "—"} />
                        <Row label="Gestellt" value={[r.unterkunft_gestellt && "Unterkunft", r.verpflegung_gestellt && "Verpflegung"].filter(Boolean).join(" · ") || "—"} />
                      </>
                    ) : (
                      <>
                        <Row label="Eintritt ab" value={fmt(r.eintritt_ab)} />
                        <Row label="Gehalt" value={r.gehalt_brutto_monat ? `${r.gehalt_brutto_monat} € brutto/Monat` : "—"} />
                        <Row label="Befristung" value={r.unbefristet ? "unbefristet" : "befristet"} />
                      </>
                    )}
                    <Row label="Position" value={stamm.positionen?.[r.position] || r.position || "—"} />
                    <Row label="Bereiche" value={labels(r.bereiche, stamm.bereiche)} />
                    <Row label="Eingegangen" value={fmt(r.created_at)} />
                  </div>

                  {r.beschreibung && (
                    <p className="text-sm text-[#575349] mt-4 pt-4 border-t border-[#E3DECE] whitespace-pre-line leading-relaxed">{r.beschreibung}</p>
                  )}

                  <div className="mt-5 pt-5 border-t border-[#E3DECE] flex flex-wrap items-end gap-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2">Status</label>
                      <select defaultValue={r.status} onChange={(e) => patchRequest(r.id, { status: e.target.value })}
                        data-testid={`request-status-${r.id}`}
                        className="bg-white border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]">
                        {Object.entries(REQ_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    </div>
                    {!isTemp && (
                      <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2">Provision einmalig (€ netto)</label>
                        <input type="number" step="50" defaultValue={r.provision_fee_eur || stamm.placement_fee_eur || 100}
                          onBlur={(e) => patchRequest(r.id, { provision_fee_eur: Number(e.target.value) || 0 })}
                          data-testid={`request-fee-${r.id}`}
                          className="bg-white border border-[#E3DECE] px-3 py-2 text-sm w-40 focus:outline-none focus:border-[#BA6D52]" />
                      </div>
                    )}
                    {isTemp && (
                      <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2">Abgerechnete Einsatztage</label>
                        <input type="number" min="1" defaultValue={r.provision_days || r.einsatztage || 1}
                          onBlur={(e) => patchRequest(r.id, { provision_days: Number(e.target.value) || 1 })}
                          data-testid={`request-days-${r.id}`}
                          className="bg-white border border-[#E3DECE] px-3 py-2 text-sm w-32 focus:outline-none focus:border-[#BA6D52]" />
                      </div>
                    )}
                    <button onClick={() => deleteRequest(r)} data-testid={`request-delete-${r.id}`}
                      className="border border-[#E3DECE] text-[#757067] hover:border-red-700 hover:text-red-700 px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                      Anfrage löschen
                    </button>
                    <button onClick={() => contractPdf(r)} data-testid={`request-contract-${r.id}`}
                      className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                      ⬇ Provisionsvereinbarung
                    </button>
                    {r.provision_contract?.signed_at ? (
                      <>
                        <span className={`${BADGE} border-green-700 text-green-800 bg-green-50`} data-testid={`request-signed-${r.id}`}>
                          Unterschrieben {fmt(r.provision_contract.signed_at)} · {r.provision_contract.name}
                        </span>
                        {r.provision_contract.file_id && (
                          <button onClick={() => openFile(r.provision_contract.file_id, `Provisionsvereinbarung_${r.request_number}_signiert.pdf`)}
                            data-testid={`request-signed-pdf-${r.id}`}
                            className="border border-[#E3DECE] text-[#575349] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                            ⬇ Signiertes PDF
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        {r.contract_sent_at && (
                          <span className={`${BADGE} border-amber-600 text-amber-800 bg-amber-50`} data-testid={`request-contract-sent-${r.id}`}>
                            Link versandt {fmt(r.contract_sent_at)}{r.contract_sent_count > 1 ? ` · ${r.contract_sent_count}×` : ""} · offen
                          </span>
                        )}
                        <button onClick={() => sendSignRequest(r, false)} data-testid={`request-sign-send-${r.id}`}
                          className="bg-[#2C2A26] text-white hover:bg-[#1f1d1a] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                          {r.contract_sent_at ? "↻ Link erneut senden" : "✍ Zum Unterschreiben senden"}
                        </button>
                        <button onClick={() => sendSignRequest(r, true)} data-testid={`request-sign-send-me-${r.id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                          Kopie an mich
                        </button>
                        <button onClick={() => copySignLink(r)} data-testid={`request-sign-copy-${r.id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                          Link kopieren
                        </button>
                      </>
                    )}
                    {r.provision_invoice_number ? (
                      <>
                        <span className={`${BADGE} ${r.provision_invoice_sent_at ? "border-green-700 text-green-800 bg-green-50" : "border-amber-600 text-amber-800 bg-amber-50"}`} data-testid={`request-invoice-number-${r.id}`}>
                          Rechnung {r.provision_invoice_number}{r.provision_invoice_sent_at ? " · versandt" : " · nicht versandt"}
                        </span>
                        <button onClick={() => invoicePdf(r)} data-testid={`request-invoice-pdf-${r.id}`}
                          className="border border-[#E3DECE] text-[#575349] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                          ⬇ Rechnung
                        </button>
                        <button onClick={() => sendInvoice(r, false)} data-testid={`request-invoice-send-${r.id}`}
                          className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                          {r.provision_invoice_sent_at ? "↻ Erneut senden" : "An Betrieb senden"}
                        </button>
                        <button onClick={() => sendInvoice(r, true)} data-testid={`request-invoice-send-me-${r.id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                          Kopie an mich
                        </button>
                      </>
                    ) : (
                      <button onClick={() => createInvoice(r)} data-testid={`request-create-invoice-${r.id}`}
                        className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
                        Provisionsrechnung erstellen
                      </button>
                    )}
                  </div>

                  <NotesField initial={r.admin_notes} onSave={(v) => patchRequest(r.id, { admin_notes: v })} testId={`request-notes-${r.id}`} />
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex gap-3">
    <span className="text-[#757067] shrink-0 min-w-[130px]">{label}</span>
    <span className="text-[#2C2A26]">{value}</span>
  </div>
);

const NotesField = ({ initial, onSave, testId }) => {
  const [val, setVal] = useState(initial || "");
  const dirty = (val || "") !== (initial || "");
  return (
    <div className="mt-5 pt-5 border-t border-[#E3DECE]">
      <label className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2">Interne Notiz</label>
      <textarea rows={2} value={val} onChange={(e) => setVal(e.target.value)} data-testid={testId}
        className="w-full bg-white border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]" />
      {dirty && (
        <button onClick={() => onSave(val)} data-testid={`${testId}-save`}
          className="mt-2 bg-[#2C2A26] text-white hover:bg-[#1f1d1a] px-4 py-2 text-[10px] uppercase tracking-[0.15em]">
          Notiz speichern
        </button>
      )}
    </div>
  );
};
